import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancerAccountStatusHistoryPage = () => {
  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'History ID',
    'Freelancer ID',
    'Freelancer Name',
    'Account Status',
    'Freeze From',
    'Freeze To',
    'Freeze Reason',
    'Change Timestamp',
  ];

  useEffect(() => {
    const fetchStatusHistory = async () => {
      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-account-status-history`);
        setHistoryData(response.data);
        setFilteredData(response.data);
      } catch (err) {
        setError('Failed to fetch freelancer account status history.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatusHistory();
  }, [apiBaseURL]);

  const applyFilters = (status, query, from, to) => {
    let filtered = historyData;

    if (status) {
      filtered = filtered.filter(entry => entry.account_status === status);
    }

    if (query) {
      filtered = filtered.filter(entry =>
        entry.freelancer_id.toString().includes(query)
      );
    }

    if (from && to) {
      const fromDateTime = new Date(from);
      fromDateTime.setHours(0, 0, 0, 0);
      const toDateTime = new Date(to);
      toDateTime.setHours(23, 59, 59, 999);

      filtered = filtered.filter(entry => {
        const ts = new Date(entry.change_timestamp);
        return ts >= fromDateTime && ts <= toDateTime;
      });
    }

    setFilteredData(filtered);
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    applyFilters(value, searchQuery, fromDate, toDate);
  };

  const handleSearchQueryChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    applyFilters(statusFilter, value, fromDate, toDate);
  };

  const handleFromDateChange = (e) => {
    const value = e.target.value;
    setFromDate(value);
    applyFilters(statusFilter, searchQuery, value, toDate);
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    setToDate(value);
    applyFilters(statusFilter, searchQuery, fromDate, value);
  };

  const data = filteredData.map(entry => ({
    history_id: entry.history_id,
    freelancer_id: entry.freelancer_id,
    freelancer_name: entry.freelancer_name || '-',
    account_status: entry.account_status,
    freeze_from: entry.freeze_from
      ? new Date(entry.freeze_from).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
      : '-',
    freeze_to: entry.freeze_to
      ? new Date(entry.freeze_to).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
      : '-',
    freeze_reason: entry.freeze_reason || '-',
    change_timestamp: entry.change_timestamp
      ? new Date(entry.change_timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
      : '-',
  }));

  const handleDownloadExcel = () => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

    const formattedDate = now.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    const filename = `Freelancer_Account_Info_History_${timestamp}.xlsx`;

    const headerRows = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Account Info History'],
      [`Downloaded on: ${formattedDate}`],
      [],
    ];

    const worksheetData = data.map(row => ({
      'History ID': row.history_id,
      'Freelancer ID': row.freelancer_id,
      'Freelancer Name': row.freelancer_name,
      'Account Status': row.account_status,
      'Freeze From': row.freeze_from,
      'Freeze To': row.freeze_to,
      'Freeze Reason': row.freeze_reason,
      'Change Timestamp': row.change_timestamp,
    }));

    const worksheet = XLSX.utils.aoa_to_sheet(headerRows);
    XLSX.utils.sheet_add_json(worksheet, worksheetData, { origin: -1 });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'AccountStatusHistory');
    XLSX.writeFile(workbook, filename);
  };

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, index) => (
              <th key={index} className="px-4 py-2 text-left whitespace-nowrap">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-4 text-center text-gray-500">
                No data available
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr key={index} className="border-b hover:bg-gray-50">
                {columns.map((col, idx) => (
                  <td key={idx} className="px-4 py-2 whitespace-nowrap">
                    {row[col.toLowerCase().replace(/ /g, '_')]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="text-gray-600 text-lg">Loading account status history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="text-red-600 text-lg">{error}</span>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-gray-100 ml-64">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Freelancer Account Status History</h2>
        <button
          onClick={handleDownloadExcel}
          className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded shadow"
        >
          Download as Excel
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between gap-4">
        <div>
          <label htmlFor="statusFilter" className="mr-2 font-medium">Filter by Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="px-4 py-2 border rounded"
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Freezed">Freezed</option>
            <option value="Blocked">Blocked</option>
          </select>
        </div>

        <div>
          <label htmlFor="search" className="mr-2 font-medium">Search by Freelancer ID:</label>
          <input
            type="text"
            id="search"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            className="px-4 py-2 border rounded"
            placeholder="Enter Freelancer ID"
          />
        </div>

        <div className="flex gap-2">
          <div>
            <label htmlFor="fromDate" className="mr-1 font-medium">From:</label>
            <input
              type="date"
              id="fromDate"
              value={fromDate}
              onChange={handleFromDateChange}
              className="px-4 py-2 border rounded"
            />
          </div>

          <div>
            <label htmlFor="toDate" className="mr-1 font-medium">To:</label>
            <input
              type="date"
              id="toDate"
              value={toDate}
              onChange={handleToDateChange}
              className="px-4 py-2 border rounded"
            />
          </div>
        </div>
      </div>

      <Table columns={columns} data={data} />
    </div>
  );
};

export default FreelancerAccountStatusHistoryPage;
